// Care host Meta events: Registration Page Visit, Registration, Registration Success, and the funnel
// steps after sign-up: ConsentSigned, IntakeSubmitted, PlanScreen, PaymentScreen.
//
// Each event is sent twice with one shared event id, and Meta keeps one:
//   1. browser: a dataLayer push. GTM-PS4VV6T turns it into a Meta pixel event and passes
//      `event_id` as the pixel's Event ID.
//   2. server: POST /api/meta-event (functions/care careMetaEvent), which sends the same event
//      through Meta's Conversions API, so ad blockers and in-app browsers don't lose it.
//
// Only runs on the care hosts. register.yourdoctors.online keeps its GTM-only tracking unchanged.
// Nothing here throws: tracking must never block sign-up.
//
// The two login events use accountEventId(name, uid), an id tied to the account, because the server
// accepts only that id for them: a repeat of the same account's event then carries the same id and
// Meta keeps one.
//
// Usage:
//   trackCareEvent('RegistrationPageVisit')
//   trackCareStepOnce('PlanScreen', () => (auth.currentUser ? auth.currentUser.getIdToken() : null))
//   trackCareEvent('Registration', { uid: user.uid, getIdToken: () => user.getIdToken(), extra: { email } })
//   const id = await accountEventId('RegistrationSuccess', uid); sendServerCopy('RegistrationSuccess', id, getIdToken)
// Test mode: open any care page with ?meta_test=TEST12345. The code is kept for the tab and sent with
// the SERVER copies only, which Meta then keeps out of reporting. The pixel copies still count.
import { newSessionToken } from './places'

export const CARE_HOSTS = new Set([
  'care.yourdoctors.online',
  'ydo-care.web.app',
  'ydo-care.firebaseapp.com',
  'ydo-care-stg.web.app',
  'ydo-care-stg.firebaseapp.com',
])

// dataLayer event names GTM listens for. RegistrationSuccess reuses the existing
// `registrationSuccess` push, which also drives the Google Ads and Reddit sign-up tags.
// The four funnel steps after sign-up (owner 2026-10-14) are care-only pushes; GTM sends each to the
// Meta pixel and to Google Analytics. Only the step name travels, never what the patient typed.
export const DL_EVENT = {
  RegistrationPageVisit: 'careRegistrationPageVisit',
  Registration: 'careRegistration',
  RegistrationSuccess: 'registrationSuccess',
  ConsentSigned: 'careConsentSigned',
  IntakeSubmitted: 'careIntakeSubmitted',
  PlanScreen: 'carePlanScreen',
  PaymentScreen: 'carePaymentScreen',
}

const TEST_KEY = 'ydo_meta_test'
const TEST_CODE = /^TEST[A-Z0-9]{1,20}$/

/** @returns {boolean} true when the page is served from a care host. */
export function isCareHost(hostname = window.location.hostname) {
  return CARE_HOSTS.has(hostname)
}

/** @returns {string} a random UUID used as the shared browser/server event id. */
export const newEventId = newSessionToken

/** @returns {string|null} the named cookie's value (used for Meta's _fbp and _fbc). */
export function readCookie(name) {
  try {
    const hit = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`))
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null
  } catch (e) {
    return null
  }
}

const LANDING_KEY = 'ydo_landing_params'
// The ad tags worth keeping: utm_* plus the ad networks' click ids (the server keeps the same set).
const AD_TAG =
  /^(utm_[a-z0-9_]{1,40}|fbclid|gclid|gbraid|wbraid|msclkid|ttclid)$/
const CLICK_ID = /^(fbclid|gclid|gbraid|wbraid|msclkid|ttclid)$/
// Fallback when sessionStorage fails (private mode, full storage, some in-app browsers): lasts until
// reload.
let memoryLanding = null

/** Clears the in-memory copy of the landing tags (tests only). */
export function resetLandingMemory() {
  memoryLanding = null
}

/**
 * @param {*} params any object
 * @returns {object|null} only its ad tags with non-empty string values, or null when none
 */
export function adTags(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params))
    return null
  const tags = {}
  Object.keys(params).forEach((k) => {
    if (AD_TAG.test(k) && typeof params[k] === 'string' && params[k])
      tags[k] = params[k]
  })
  return Object.keys(tags).length ? tags : null
}

const hasClickId = (tags) =>
  !!tags && Object.keys(tags).some((k) => CLICK_ID.test(k))

/**
 * @returns {object|null} the kept tags: this page load's copy first (always the newest), then
 *   sessionStorage (survives a reload).
 */
function keptTags() {
  if (memoryLanding) return memoryLanding
  try {
    const raw = window.sessionStorage.getItem(LANDING_KEY)
    return raw ? adTags(JSON.parse(raw)) : null
  } catch (e) {
    return null // storage blocked or unreadable
  }
}

/**
 * Keeps the ad tags (utm_*, fbclid, gclid...) of the tab's latest ad link on the care hosts, so they
 * survive a reload before sign-up. A link without ad tags leaves the kept set alone. A later ad link
 * replaces it, except that a link with utm tags but no click id (an email, an internal link) never
 * replaces a set that has an ad click id. users/{uid}/utmParams follows the link as before. Does nothing off care.
 * @param {object|null} params the link parameters routes.js collected (or null)
 */
export function rememberLandingParams(params) {
  if (!isCareHost()) return
  const tags = adTags(params)
  if (!tags) return
  if (!hasClickId(tags) && hasClickId(keptTags())) return
  memoryLanding = tags
  try {
    window.sessionStorage.setItem(LANDING_KEY, JSON.stringify(tags))
  } catch (e) {
    // storage blocked or full: memoryLanding still has them for this page load
  }
}

/** @returns {object|null} the tab's kept ad tags on care, or null. */
export function storedLandingParams() {
  if (!isCareHost()) return null
  return keptTags()
}

/** @returns {string|null} the Meta test code from ?meta_test= or earlier in this tab. */
export function testEventCode() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('meta_test')
    if (fromUrl && TEST_CODE.test(fromUrl)) {
      window.sessionStorage.setItem(TEST_KEY, fromUrl)
      return fromUrl
    }
    const kept = window.sessionStorage.getItem(TEST_KEY)
    return kept && TEST_CODE.test(kept) ? kept : null
  } catch (e) {
    return null
  }
}

/**
 * Sends the server copy of one event to /api/meta-event. Resolves to the relay's JSON answer, or
 * null on any failure. keepalive lets the request finish when the page navigates away.
 * @param {string} eventName any key of DL_EVENT
 * @param {string} eventId the id already pushed to the dataLayer
 * @param {function} [getIdToken] resolves to the Firebase ID token (every event except
 *   RegistrationPageVisit); a null token skips the server copy
 */
export async function sendServerCopy(eventName, eventId, getIdToken) {
  try {
    const headers = { 'Content-Type': 'application/json' }
    if (getIdToken) {
      // Signed out (or the token call failed): the server would refuse the copy, so don't send it.
      const token = await getIdToken()
      if (!token) return null
      headers.Authorization = `Bearer ${token}`
    }
    const body = {
      eventName,
      eventId,
      // Origin + path only: the query can hold a handoff's ?medical_issue= text (routes.js).
      url: `${window.location.origin}${window.location.pathname}`,
      fbp: readCookie('_fbp'),
      fbc: readCookie('_fbc'),
    }
    const code = testEventCode()
    if (code) body.testEventCode = code
    // The ad click as it arrived; the server keeps it in the account's sent note (not sent to Meta).
    const landing = storedLandingParams()
    if (landing) body.landing = landing
    const res = await fetch('/api/meta-event', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    })
    return await res.json()
  } catch (e) {
    return null
  }
}

const ID_PREFIX = { Registration: 'reg', RegistrationSuccess: 'regok' }

/**
 * The event id for a login event, tied to the account: "<prefix>-<first 32 hex of
 * sha256('<eventName>:<uid>')>". Must match accountEventId in functions/care/metaEvent.js.
 * @param {string} eventName Registration | RegistrationSuccess
 * @param {string} uid Firebase uid
 * @returns {Promise<string|null>} the id, or null when the browser has no Web Crypto (then the
 *   caller sends the pixel copy without an id and skips the server copy, which would be refused)
 */
export async function accountEventId(eventName, uid) {
  try {
    const bytes = new TextEncoder().encode(`${eventName}:${uid}`)
    const digest = await window.crypto.subtle.digest('SHA-256', bytes)
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    return `${ID_PREFIX[eventName]}-${hex.slice(0, 32)}`
  } catch (e) {
    return null
  }
}

/**
 * Sends one care event to the pixel (via the dataLayer) and to the server relay.
 * @param {string} eventName RegistrationPageVisit | Registration | ConsentSigned | IntakeSubmitted |
 *   PlanScreen | PaymentScreen
 * @param {object} [opts] { uid, getIdToken, extra } uid is required for Registration (its id is
 *   tied to the account); extra fields go on the dataLayer push (e.g. email for advanced matching)
 * @returns {Promise<string|null>} the event id, or null when not on a care host or no id was made.
 */
export async function trackCareEvent(
  eventName,
  { uid, getIdToken, extra } = {},
) {
  // Callers fire and forget, so nothing may reject: any surprise resolves to null.
  try {
    if (!isCareHost()) return null
    const eventId = uid ? await accountEventId(eventName, uid) : newEventId()
    try {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: DL_EVENT[eventName],
        ...(eventId ? { event_id: eventId } : {}),
        ...extra,
      })
    } catch (e) {
      // GTM missing or blocked: the server copy still goes out
    }
    if (eventId) sendServerCopy(eventName, eventId, getIdToken)
    return eventId
  } catch (e) {
    return null
  }
}

// Funnel steps after sign-up count once per page load: the plan list remounts after Back from
// payment or a profile fix, and the payment screen opens again from the chat queue.
const stepsSent = new Set()
/** Forgets which funnel steps were sent (tests only). */
export function resetCareSteps() {
  stepsSent.clear()
}
/**
 * Sends one funnel step (ConsentSigned, IntakeSubmitted, PlanScreen, PaymentScreen) once per page
 * load on a care host: a random event id, the pixel copy via the dataLayer and the server copy.
 * @param {string} eventName the step
 * @param {function} getIdToken resolves to the signed-in user's Firebase ID token, or null
 * @returns {Promise<string|null>|null} the event id, or null when not sent
 */
export function trackCareStepOnce(eventName, getIdToken) {
  if (stepsSent.has(eventName) || !isCareHost()) return null
  stepsSent.add(eventName)
  return trackCareEvent(eventName, { getIdToken })
}

// The sign-up screen can mount more than once per page load (StrictMode, back navigation);
// Registration Page Visit counts page loads, like register's GTM rule does.
let pageVisitSent = false
/** Sends RegistrationPageVisit once per page load on a care host. @returns {Promise<string|null>|null} */
export function trackCarePageVisitOnce() {
  if (pageVisitSent || !isCareHost()) return null
  pageVisitSent = true
  return trackCareEvent('RegistrationPageVisit')
}
